// hooks/useS3Upload.ts
import { IPreSignResponse } from '@/api/modules/files/types'
import { ref, computed } from 'vue'
import type { Ref } from 'vue'

export interface S3UploadOptions {
  presignedUrlEndpoint: string
  maxFileSize?: number // в байтах
  fileTypes?: string[]
  multiple?: boolean
  headers?: Record<string, string> | (() => Record<string, string>)
  onProgress?: (progress: number) => void
  onSuccess?: (urls: string[]) => void
  onError?: (error: Error) => void
}

export interface S3UploadResult {
  urls: string[]
  fileNames: string[]
  fileIds: Array<string | undefined>
  sizes: number[]
  errors: string[]
}

export interface UploadedFile {
  file: File
  previewUrl?: string
  fileId?: string
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  url?: string
  error?: string
}

export function useS3Upload(options: S3UploadOptions) {
  const fileTypes = ref<string[]>()

  const files: Ref<UploadedFile[]> = ref([])
  const isUploading = ref(false)
  const uploadProgress = ref(0)

  const allowedFileTypes = computed(() => fileTypes.value || options.fileTypes || [])

  const totalProgress = computed(() => {
    if (files.value.length === 0) return 0
    const total = files.value.reduce((sum, file) => sum + file.progress, 0)
    return Math.round(total / files.value.length)
  })

  // Валидация файла
  const validateFile = (file: File): string | null => {
    if (options.maxFileSize && file.size > options.maxFileSize) {
      return `Файл слишком большой. Максимальный размер: ${formatFileSize(options.maxFileSize)}`
    }

    if (allowedFileTypes.value && allowedFileTypes.value.length > 0) {
      const fileType = file.type || getFileExtension(file.name)
      const isAllowed = allowedFileTypes.value.some((type) => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase())
        }
        return fileType.includes(type.replace('*', ''))
      })

      if (!isAllowed) {
        return `Недопустимый тип файла. Разрешены: ${allowedFileTypes.value.join(', ')}`
      }
    }

    return null
  }

  // Получение расширения файла
  const getFileExtension = (filename: string): string => {
    return '.' + filename.split('.').pop()?.toLowerCase() || ''
  }

  // Форматирование размера файла
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Создание превью для изображений
  const createPreview = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve('')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        resolve((e.target?.result as string) || '')
      }
      reader.readAsDataURL(file)
    })
  }

  // Получение presigned URL (если используется)
  const getPresignedUrl = async (
    filename: string,
    content_type: string,
    size: number,
  ): Promise<IPreSignResponse> => {
    try {
      const response = await fetch(options.presignedUrlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: JSON.stringify({
          filename,
          content_type,
          size,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to get presigned URL')
      }

      const data = await response.json()
      return data
    } catch (error) {
      throw new Error(`Error getting presigned URL: ${error}`)
    }
  }

  // Загрузка одного файла
  const uploadFile = async (file: File, index: number): Promise<string> => {
    const validationError = validateFile(file)
    if (validationError) {
      throw new Error(validationError)
    }

    // Обновляем статус файла
    files.value[index].status = 'uploading'

    try {
      // Используем presigned URL
      const dataPresigne = await getPresignedUrl(file.name, file.type, file.size)

      // Создаем FormData для загрузки
      const formData = new FormData()

      Object.keys(dataPresigne.fields).map((key) => {
        formData.append(key, dataPresigne.fields[key])
      })

      formData.append('file', file)

      // Используем XMLHttpRequest для отслеживания прогресса
      return new Promise((resolve, reject) => {
        const xhr: XMLHttpRequest = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (event: any) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            files.value[index].progress = progress

            // Вызываем callback прогресса
            if (options.onProgress) {
              options.onProgress(totalProgress.value)
            }
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            files.value[index].status = 'success'
            files.value[index].progress = 100
            files.value[index].url = dataPresigne.upload_url + dataPresigne.fields.key
            files.value[index].fileId = dataPresigne.file_id
            resolve(dataPresigne.upload_url)
          } else {
            files.value[index].status = 'error'
            files.value[index].error = `Upload failed with status ${xhr.status}`
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => {
          files.value[index].status = 'error'
          files.value[index].error = 'Network error'
          reject(new Error('Network error'))
        })

        xhr.open('POST', dataPresigne.upload_url)

        // Для S3 может потребоваться дополнительные заголовки
        if (!options.presignedUrlEndpoint) {
          xhr.setRequestHeader('Content-Type', file.type)
        }

        xhr.send(formData)
      })
    } catch (error) {
      files.value[index].status = 'error'
      files.value[index].error = error instanceof Error ? error.message : 'Unknown error'
      console.log(error)
      throw error
    }
  }

  // Загрузка нескольких файлов
  const uploadFiles = async (selectedFiles: File[]): Promise<S3UploadResult> => {
    isUploading.value = true
    uploadProgress.value = 0

    // Инициализируем массив файлов
    files.value = await Promise.all(
      selectedFiles.map(async (file) => {
        const previewUrl = await createPreview(file)
        return {
          file,
          previewUrl,
          progress: 0,
          status: 'pending' as const,
        }
      }),
    )

    const result: S3UploadResult = {
      urls: [],
      fileNames: [],
      fileIds: [],
      sizes: [],
      errors: [],
    }

    try {
      const uploadPromises = files.value.map((_, index) =>
        uploadFile(files.value[index].file, index)
          .then((url) => {
            result.urls.push(url)
            result.fileNames.push(files.value[index].file.name)
            result.fileIds.push(files.value[index].fileId)
            result.sizes.push(files.value[index].file.size)
          })
          .catch((error) => {
            result.errors.push(error.message)
          }),
      )

      await Promise.all(uploadPromises)

      if (options.onSuccess && result.urls.length > 0) {
        options.onSuccess(result.urls)
      }

      if (options.onError && result.errors.length > 0) {
        options.onError(new Error(result.errors.join(', ')))
      }

      return result
    } finally {
      isUploading.value = false
    }
  }

  // Очистка файлов
  const clearFiles = () => {
    files.value = []
  }

  // Удаление файла по индексу
  const removeFile = (index: number) => {
    files.value.splice(index, 1)
  }

  const setFileTypes = (value: string[]) => (fileTypes.value = value)

  return {
    // Состояние
    files,
    isUploading,
    uploadProgress: totalProgress,

    // Методы
    uploadFiles,
    uploadFile,
    clearFiles,
    removeFile,
    validateFile,
    setFileTypes,

    // Утилиты
    formatFileSize,
    getFileExtension,
  }
}
